import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import {
  HiOutlineArrowRight,
  HiOutlineCash,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineGift,
  HiOutlineLockClosed,
  HiOutlineMail,
  HiOutlineOfficeBuilding,
  HiOutlineShieldCheck,
  HiOutlineUser,
  HiOutlineUserAdd,
  HiOutlineUsers,
} from "react-icons/hi";
import Labels from "../../services/apiLabels";
import { POST } from "../../constant/apiMethods";
import { SIGN_UP, LOGIN, TOASTER_SHOW, toaster_e } from "../../constant";
import {
  captureReferralCodeFromSearch,
  clearPendingReferralCode,
  getPendingReferralCode,
  readSignupEmailFromSearch,
} from "../../utils/userIdentity";
import {
  USER_TYPE_DEALER,
  isDealerUser,
  pickUserType,
  signupRoleFromSearch,
  userTypeFromSignupRole,
  redirectAfterLogin,
} from "../../utils/userTypes";
import { markOnboardingTourPending } from "../../utils/onboardingTour";
import OverLayLoader from "../../components/OverLayLoader";
import GoogleAuthButton from "../../components/GoogleAuthButton";
import "../Login Page/client-login.css";

const publicUrl = process.env.PUBLIC_URL || "";
const VISUAL_IMAGE = `${publicUrl}/images/hero-hd-showroom.jpg`;

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function validateSignupFields({
  firstName,
  lastName,
  email,
  username,
  password,
  confirmPassword,
}) {
  const errors = {};
  if (!String(firstName || "").trim()) {
    errors.firstName = "Please enter your first name.";
  }
  if (!String(lastName || "").trim()) {
    errors.lastName = "Please enter your last name.";
  }
  const nextEmail = String(email || "").trim();
  if (!nextEmail) errors.email = "Please enter your email.";
  else if (!isValidEmail(nextEmail)) {
    errors.email = "Please enter a valid email address.";
  }
  if (!String(username || "").trim()) {
    errors.username = "Please enter a user name.";
  }
  if (!password) errors.password = "Please enter a password.";
  if (!confirmPassword) {
    errors.confirmPassword = "Please confirm your password.";
  } else if (password && password !== confirmPassword) {
    errors.confirmPassword = "Passwords do not match.";
  }
  return errors;
}

function Signup(props) {
  const { auth, signUp, googleSignUp, toasterShow, SIGN_UP_done } = props;
  const history = useHistory();
  const location = useLocation();
  const valuationRequired = Boolean(location.state?.valuationRequired);

  const [accountRole, setAccountRole] = useState(() =>
    signupRoleFromSearch(location.search)
  );
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState(() =>
    readSignupEmailFromSearch(location.search)
  );
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [refCode, setRefCode] = useState(() =>
    getPendingReferralCode(location.search)
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const pendingAutoLoginRef = useRef(false);

  const isDealer = accountRole === "dealer";

  const fieldErrors = useMemo(
    () =>
      validateSignupFields({
        firstName,
        lastName,
        email,
        username,
        password,
        confirmPassword,
      }),
    [firstName, lastName, email, username, password, confirmPassword]
  );
  const canSubmit = Object.keys(fieldErrors).length === 0;

  const syncErrors = (nextValues = {}) => {
    setErrors(
      validateSignupFields({
        firstName: nextValues.firstName ?? firstName,
        lastName: nextValues.lastName ?? lastName,
        email: nextValues.email ?? email,
        username: nextValues.username ?? username,
        password: nextValues.password ?? password,
        confirmPassword: nextValues.confirmPassword ?? confirmPassword,
      })
    );
  };

  const markTouched = (key) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
    syncErrors();
  };

  const showError = (key) => (touched[key] ? errors[key] : "");

  useEffect(() => {
    setAccountRole(signupRoleFromSearch(location.search));
  }, [location.search]);

  useEffect(() => {
    const code = captureReferralCodeFromSearch(location.search);
    if (code) setRefCode(code);
    const inviteEmail = readSignupEmailFromSearch(location.search);
    if (inviteEmail) setEmail(inviteEmail);
  }, [location.search]);

  const navigateAfterAuth = useCallback((response) => {
    clearPendingReferralCode();
    pendingAutoLoginRef.current = false;
    const payload = response?.data || response || auth || {};
    // Signup role wins when the API login payload has not hydrated `type` yet.
    const authForRedirect =
      isDealer || isDealerUser(payload) || isDealerUser(payload?.user || {})
        ? {
            ...payload,
            type:
              pickUserType(payload, payload?.user) ?? USER_TYPE_DEALER,
          }
        : payload;
    markOnboardingTourPending(
      isDealer ? USER_TYPE_DEALER : userTypeFromSignupRole(accountRole)
    );
    const from =
      location.state?.from ||
      (valuationRequired && !isDealerUser(authForRedirect)
        ? { pathname: "/" }
        : null);
    history.replace(redirectAfterLogin(authForRedirect, from));
  }, [accountRole, auth, history, location.state, valuationRequired, isDealer]);

  // Fallback if signup completes without a callBack (token appears in auth).
  useEffect(() => {
    if (!pendingAutoLoginRef.current || !auth?.token) return;
    pendingAutoLoginRef.current = false;
    navigateAfterAuth({ data: auth });
  }, [auth, navigateAfterAuth]);

  // Clear signup-done token noise so a remount does not re-trigger flows.
  useEffect(() => {
    if (!SIGN_UP_done || SIGN_UP_done === false) return;
    if (SIGN_UP_done && typeof SIGN_UP_done === "object" && SIGN_UP_done.token) {
      SIGN_UP_done.token = "";
    }
  }, [SIGN_UP_done]);

  const selectRole = (role) => {
    setAccountRole(role);
    try {
      const params = new URLSearchParams(location.search || "");
      params.set("role", role);
      history.replace({
        pathname: "/signup",
        search: `?${params.toString()}`,
        state: location.state,
      });
    } catch (e) {
      /* ignore */
    }
  };

  const signUpHandler = (event) => {
    event.preventDefault();
    const nextErrors = validateSignupFields({
      firstName,
      lastName,
      email,
      username,
      password,
      confirmPassword,
    });
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      username: true,
      password: true,
      confirmPassword: true,
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const first_name = firstName.trim();
    const last_name = lastName.trim();
    const nextEmail = email.trim();
    const nextUsername = username.trim();
    const ref_code = String(refCode || "").trim();

    const data = {
      first_name,
      last_name,
      username: nextUsername,
      password,
      email: nextEmail,
      type: userTypeFromSignupRole(accountRole),
    };
    if (ref_code) {
      data.ref_code = ref_code;
    }
    pendingAutoLoginRef.current = true;
    signUp(data, navigateAfterAuth);
  };

  const onGoogleSuccessNavigate = () => {
    navigateAfterAuth();
  };

  const onGoogleCredential = (credential, error) => {
    if (error || !credential) {
      toasterShow({
        show: true,
        autoHide: true,
        type: toaster_e,
        heading: "Google sign-up",
        description:
          error?.message || "Google sign-up was cancelled or failed.",
      });
      return;
    }
    const payload = {
      id_token: credential,
      type: userTypeFromSignupRole(accountRole),
    };
    const ref_code = String(refCode || "").trim();
    if (ref_code) {
      payload.ref_code = ref_code;
    }
    googleSignUp(payload, onGoogleSuccessNavigate);
  };

  if (auth.token) {
    return <OverLayLoader alwaysShow={true} />;
  }

  return (
    <div className="pi-login pi-login--signup">
      <div className="pi-login__fx" aria-hidden="true">
        <span className="pi-login__glow pi-login__glow--a" />
        <span className="pi-login__glow pi-login__glow--b" />
        <span className="pi-login__grid" />
      </div>

      <div className="pi-login__inner">
        <div className="pi-login__shell">
          <section className="pi-login__panel pi-login__panel--form">
            <header className="pi-login__head">
              <p className="pi-label">Create account</p>
              <h1>Sign up</h1>
              <p className="pi-login__lead">
                {valuationRequired
                  ? "Create an account to continue your vehicle valuation."
                  : "One signup for client and dealer accounts. Choose your role below."}
              </p>
            </header>

            {valuationRequired ? (
              <div className="pi-login__alert" role="status">
                Please sign up or log in to get your vehicle valuation.
              </div>
            ) : null}

            {refCode ? (
              <div className="pi-login__ref-banner" role="status">
                <HiOutlineGift size={16} aria-hidden />
                Referral code applied: <strong>{refCode}</strong>
                {email ? (
                  <>
                    {" "}
                    · Invite email: <strong>{email}</strong>
                  </>
                ) : null}
              </div>
            ) : email && readSignupEmailFromSearch(location.search) ? (
              <div className="pi-login__ref-banner" role="status">
                <HiOutlineMail size={16} aria-hidden />
                Invite email applied: <strong>{email}</strong>
              </div>
            ) : null}

            <form
              className="pi-login__form"
              onSubmit={signUpHandler}
              autoComplete="on"
              noValidate
            >
              <fieldset className="pi-login__role">
                <legend className="pi-login__field-label">
                  Account type
                  <span className="pi-login__req" aria-hidden="true">
                    *
                  </span>
                </legend>
                <div
                  className="pi-login__role-toggle"
                  role="radiogroup"
                  aria-label="Account type"
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!isDealer}
                    className={
                      !isDealer
                        ? "pi-login__role-btn is-active"
                        : "pi-login__role-btn"
                    }
                    onClick={() => selectRole("client")}
                  >
                    <HiOutlineUsers size={18} aria-hidden />
                    <span>
                      <strong>Client</strong>
                      <em>Sell, value, appointments</em>
                    </span>
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isDealer}
                    className={
                      isDealer
                        ? "pi-login__role-btn is-active"
                        : "pi-login__role-btn"
                    }
                    onClick={() => selectRole("dealer")}
                  >
                    <HiOutlineOfficeBuilding size={18} aria-hidden />
                    <span>
                      <strong>Dealer</strong>
                      <em>Auctions, buy now, trade</em>
                    </span>
                  </button>
                </div>
              </fieldset>

              <div className="pi-login__row">
                <label
                  className={`pi-login__field${
                    showError("firstName") ? " is-invalid" : ""
                  }`}
                >
                  <span className="pi-login__field-label">
                    First name
                    <span className="pi-login__req" aria-hidden="true">
                      *
                    </span>
                  </span>
                  <span className="pi-login__control">
                    <HiOutlineUserAdd size={18} aria-hidden />
                    <input
                      type="text"
                      name="first_name"
                      autoComplete="given-name"
                      placeholder="First name"
                      value={firstName}
                      aria-required="true"
                      aria-invalid={Boolean(showError("firstName"))}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        if (Object.keys(touched).length) {
                          syncErrors({ firstName: e.target.value });
                        }
                      }}
                      onBlur={() => markTouched("firstName")}
                    />
                  </span>
                  {showError("firstName") ? (
                    <p className="pi-login__field-error" role="alert">
                      {showError("firstName")}
                    </p>
                  ) : null}
                </label>

                <label
                  className={`pi-login__field${
                    showError("lastName") ? " is-invalid" : ""
                  }`}
                >
                  <span className="pi-login__field-label">
                    Last name
                    <span className="pi-login__req" aria-hidden="true">
                      *
                    </span>
                  </span>
                  <span className="pi-login__control">
                    <HiOutlineUserAdd size={18} aria-hidden />
                    <input
                      type="text"
                      name="last_name"
                      autoComplete="family-name"
                      placeholder="Last name"
                      value={lastName}
                      aria-required="true"
                      aria-invalid={Boolean(showError("lastName"))}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        if (Object.keys(touched).length) {
                          syncErrors({ lastName: e.target.value });
                        }
                      }}
                      onBlur={() => markTouched("lastName")}
                    />
                  </span>
                  {showError("lastName") ? (
                    <p className="pi-login__field-error" role="alert">
                      {showError("lastName")}
                    </p>
                  ) : null}
                </label>
              </div>

              <label
                className={`pi-login__field${
                  showError("email") ? " is-invalid" : ""
                }`}
              >
                <span className="pi-login__field-label">
                  Email
                  <span className="pi-login__req" aria-hidden="true">
                    *
                  </span>
                </span>
                <span className="pi-login__control">
                  <HiOutlineMail size={18} aria-hidden />
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="Enter email"
                    value={email}
                    aria-required="true"
                    aria-invalid={Boolean(showError("email"))}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (Object.keys(touched).length) {
                        syncErrors({ email: e.target.value });
                      }
                    }}
                    onBlur={() => markTouched("email")}
                  />
                </span>
                {showError("email") ? (
                  <p className="pi-login__field-error" role="alert">
                    {showError("email")}
                  </p>
                ) : null}
              </label>

              <label
                className={`pi-login__field${
                  showError("username") ? " is-invalid" : ""
                }`}
              >
                <span className="pi-login__field-label">
                  User name
                  <span className="pi-login__req" aria-hidden="true">
                    *
                  </span>
                </span>
                <span className="pi-login__control">
                  <HiOutlineUser size={18} aria-hidden />
                  <input
                    type="text"
                    name="username"
                    autoComplete="username"
                    placeholder="Enter user name"
                    value={username}
                    aria-required="true"
                    aria-invalid={Boolean(showError("username"))}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      if (Object.keys(touched).length) {
                        syncErrors({ username: e.target.value });
                      }
                    }}
                    onBlur={() => markTouched("username")}
                  />
                </span>
                {showError("username") ? (
                  <p className="pi-login__field-error" role="alert">
                    {showError("username")}
                  </p>
                ) : null}
              </label>

              <div className="pi-login__row pi-login__row--passwords">
                <div
                  className={`pi-login__field${
                    showError("password") ? " is-invalid" : ""
                  }`}
                >
                  <span className="pi-login__field-label" id="signup-password-label">
                    Password
                    <span className="pi-login__req" aria-hidden="true">
                      *
                    </span>
                  </span>
                  <span className="pi-login__control">
                    <HiOutlineLockClosed size={18} aria-hidden />
                    <input
                      id="signup-password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      autoComplete="new-password"
                      placeholder="Enter password"
                      value={password}
                      aria-required="true"
                      aria-invalid={Boolean(showError("password"))}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (Object.keys(touched).length) {
                          syncErrors({ password: e.target.value });
                        }
                      }}
                      onBlur={() => markTouched("password")}
                      aria-labelledby="signup-password-label"
                    />
                    <button
                      type="button"
                      className="pi-login__toggle-password"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? (
                        <HiOutlineEyeOff size={18} aria-hidden />
                      ) : (
                        <HiOutlineEye size={18} aria-hidden />
                      )}
                    </button>
                  </span>
                  {showError("password") ? (
                    <p className="pi-login__field-error" role="alert">
                      {showError("password")}
                    </p>
                  ) : null}
                </div>

                <div
                  className={`pi-login__field${
                    showError("confirmPassword") ? " is-invalid" : ""
                  }`}
                >
                  <span
                    className="pi-login__field-label"
                    id="signup-confirm-password-label"
                  >
                    Confirm password
                    <span className="pi-login__req" aria-hidden="true">
                      *
                    </span>
                  </span>
                  <span className="pi-login__control">
                    <HiOutlineLockClosed size={18} aria-hidden />
                    <input
                      id="signup-confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirm_password"
                      autoComplete="new-password"
                      placeholder="Re-enter password"
                      value={confirmPassword}
                      aria-required="true"
                      aria-invalid={Boolean(showError("confirmPassword"))}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (Object.keys(touched).length) {
                          syncErrors({ confirmPassword: e.target.value });
                        }
                      }}
                      onBlur={() => markTouched("confirmPassword")}
                      aria-labelledby="signup-confirm-password-label"
                    />
                    <button
                      type="button"
                      className="pi-login__toggle-password"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      aria-label={
                        showConfirmPassword
                          ? "Hide confirm password"
                          : "Show confirm password"
                      }
                      aria-pressed={showConfirmPassword}
                    >
                      {showConfirmPassword ? (
                        <HiOutlineEyeOff size={18} aria-hidden />
                      ) : (
                        <HiOutlineEye size={18} aria-hidden />
                      )}
                    </button>
                  </span>
                  {showError("confirmPassword") ? (
                    <p className="pi-login__field-error" role="alert">
                      {showError("confirmPassword")}
                    </p>
                  ) : null}
                </div>
              </div>

              <label className="pi-login__field">
                <span className="pi-login__field-label">
                  Referral code <em>(optional)</em>
                </span>
                <span className="pi-login__control">
                  <HiOutlineGift size={18} aria-hidden />
                  <input
                    type="text"
                    name="ref_code"
                    autoComplete="off"
                    placeholder="Enter referral code"
                    value={refCode}
                    onChange={(e) => setRefCode(e.target.value)}
                  />
                </span>
              </label>

              <button
                type="submit"
                className="pi-login__submit"
                disabled={!canSubmit || Boolean(auth.creating || auth.loging)}
              >
                {auth.creating
                  ? "Creating account…"
                  : isDealer
                    ? "Create dealer account"
                    : "Create client account"}
                {!auth.creating ? (
                  <HiOutlineArrowRight size={18} aria-hidden />
                ) : null}
              </button>
            </form>

            <div className="pi-login__or" aria-hidden="true">
              <span>or</span>
            </div>

            <GoogleAuthButton
              text="signup_with"
              disabled={Boolean(auth.creating || auth.loging)}
              onCredential={onGoogleCredential}
            />

            <p className="pi-login__footer-note">
              Already have an account?{" "}
              <Link
                to={{
                  pathname: "/login",
                  state: location.state,
                }}
              >
                Log in
              </Link>
            </p>
          </section>

          <aside
            className="pi-login__panel pi-login__panel--visual"
            style={{ backgroundImage: `url(${VISUAL_IMAGE})` }}
            aria-hidden="true"
          >
            <span className="pi-login__visual-shade" />
            <div className="pi-login__visual-copy">
              <p className="pi-login__visual-kicker">AutoTrade Port</p>
              <h2>
                {isDealer ? (
                  <>
                    Join as a dealer.
                    <span> Access the trade floor.</span>
                  </>
                ) : (
                  <>
                    Start selling.
                    <span> Earn with referrals.</span>
                  </>
                )}
              </h2>
              <ul className="pi-login__visual-points">
                <li>
                  <HiOutlineShieldCheck size={16} />
                  {isDealer
                    ? "Verified dealer access in minutes"
                    : "Secure client account in minutes"}
                </li>
                <li>
                  <HiOutlineCash size={16} />
                  Credits land in your wallet
                </li>
                <li>
                  <HiOutlineGift size={16} />
                  Optional referral code on signup
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  SIGN_UP_done: state.auth.SIGN_UP_done,
});

const mapDispatchToProps = (dispatch) => ({
  signUp: (data, callBack) =>
    dispatch({
      type: SIGN_UP,
      payload: {
        type: SIGN_UP,
        method: POST,
        label: Labels.signUp,
        data,
        callBack,
      },
    }),
  googleSignUp: (data, callBack) =>
    dispatch({
      type: LOGIN,
      payload: {
        type: LOGIN,
        method: POST,
        label: Labels.googleAuth,
        hideOverlayLoader: true,
        data,
        callBack,
      },
    }),
  toasterShow: (data) =>
    dispatch({
      type: TOASTER_SHOW,
      payload: {
        ...data,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Signup);
